'use client';

import { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, OutlinedInput, OutlinedInputProps, SvgIconProps, SxProps } from '@mui/material';
import { recordEvent } from '@qb/frontend/analytics/ga4';
import { useTypewriterEffect } from '@qb/frontend/hooks/useTypewriterEffect';
import { TenantAliasProps } from '@qb/frontend/types/tenantAlias.types';
import { getHostFromWindow } from '@qb/frontend/utils/hosts/getHostFromWindow';
import { mergeSx } from '@qb/frontend/utils/mergeSx';
import { spsClientQueries } from '@qb/httpRequest/spsClientQueries';
import { IconMagic } from './IconMagic';
export type OmniSearchInputG4AEventsProps = {
  searchInputName: '' | 'SPSHeroSearch' | 'SPSSearchAndChat' | 'WidgetNavbarSearch';
};
type OmniSearchInputProps = TenantAliasProps & OutlinedInputProps & OmniSearchInputG4AEventsProps & {
  isPlaceholderAnimationEnabled?: boolean;
  magicIconSx?: SxProps;
  handlerRedirect: (href: string) => void;
  magnifyingGlassColor?: SvgIconProps['color'];
};
export const OmniSearchInput = ({
  onFocus,
  onKeyDown,
  onChange,
  searchInputName,
  sx,
  magicIconSx,
  tenantAlias,
  isPlaceholderAnimationEnabled,
  handlerRedirect,
  magnifyingGlassColor = 'primary',
  ...rest
}: OmniSearchInputProps) => {
  const queryClient = useQueryClient();
  const {
    textToDisplay: animatedPlaceholderText
  } = useTypewriterEffect({
    text: 'Search by part # or keyword',
    isEnabled: isPlaceholderAnimationEnabled
  });
  const onKeyDownHandler = useCallback<KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>>(event => {
    const query = event.currentTarget.value;
    if (event.key === 'Enter' && query) {
      event.currentTarget.blur();
      handlerRedirect(`/search?query=${query}`);
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  }, [handlerRedirect, onKeyDown]);
  const onFocusHandler = useCallback<FocusEventHandler<HTMLInputElement>>(event => {
    if (onFocus) {
      onFocus(event);
    }
    if (searchInputName) {
      const query = event.currentTarget.value;
      recordEvent('search_focus', {
        attributes: {
          search_phrase: query,
          search_type: 'freeform',
          search_inputbox_name: searchInputName
        }
      });
    }
  }, [onFocus, searchInputName]);
  const onChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
    if (onChange) {
      onChange(event);
    }
  }, [onChange]);
  const onMouseEnterHandler = useCallback(() => {
    queryClient.prefetchQuery({
      ...spsClientQueries.partCategory.searchCategories(getHostFromWindow(), tenantAlias),
      staleTime: hoursToMilliseconds(1)
    });
  }, [queryClient, tenantAlias]);
  return <OutlinedInput placeholder={animatedPlaceholderText} aria-label="Part search input" size="small" startAdornment={<InputAdornment sx={{
    pl: 0.5,
    mr: 0
  }} position="start">
          <SearchIcon color={magnifyingGlassColor} />
        </InputAdornment>} endAdornment={<IconMagic sx={magicIconSx} />} inputProps={{
    sx: {
      height: 24,
      pl: 1,
      py: 0.75,
      '&::placeholder': {
        color: 'text.secondary'
      },
      '&:focus::placeholder': {
        color: 'transparent'
      }
    },
    type: 'search',
    enterKeyHint: 'search'
  }} sx={mergeSx({
    zIndex: 'var(--qb-omni-search-z-index, calc(var(--mui-zIndex-appBar) + 1))',
    width: '100%',
    bgcolor: 'common.white',
    pl: 1,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: 2
    },
    '&.MuiOutlinedInput-root': {
      pr: 0
    }
  }, sx)} {...rest} onKeyDown={onKeyDownHandler} onFocus={onFocusHandler} onChange={onChangeHandler} onMouseEnter={onMouseEnterHandler} data-sentry-element="OutlinedInput" data-sentry-component="OmniSearchInput" data-sentry-source-file="OmniSearchInput.tsx" />;
};